import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";
import { getStoredAffiliateCode } from "@/hooks/useAffiliateTracking";
import CountrySelectionModal, { getCountryNames } from "./CountrySelectionModal";
import { z } from "zod";

const emailSchema = z.string().email("Email invalide").max(255, "Email trop long");

interface StripeButtonWithCountriesProps {
  productType: "3months" | "6months" | "12months" | "test1euro";
  children: React.ReactNode;
  className?: string;
  size?: "default" | "sm" | "lg" | "icon";
  variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link" | "vip";
}

type Step = "countries" | "email";

const StripeButtonWithCountries = ({ 
  productType, 
  children, 
  className, 
  size = "lg", 
  variant = "default" 
}: StripeButtonWithCountriesProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [countryModalOpen, setCountryModalOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [step, setStep] = useState<Step>("countries");

  const handleButtonClick = () => {
    setStep("countries");
    setCountryModalOpen(true);
  };

  const handleCountriesConfirm = (countries: string[]) => {
    setSelectedCountries(countries);
    setCountryModalOpen(false);
    // Passer à l'étape email
    setStep("email");
    setEmailDialogOpen(true);
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError("");

    // Valider l'email
    const validation = emailSchema.safeParse(email.trim());
    if (!validation.success) {
      setEmailError(validation.error.errors[0].message);
      return;
    }

    setIsLoading(true);

    try {
      // Récupérer le code affilié du localStorage
      const affiliateCode = getStoredAffiliateCode() || '';
      
      console.log("📧 Email collecté avant Stripe:", email.trim());
      console.log("🏷️ Code affilié:", affiliateCode || "(aucun)");

      // IMPORTANT: On ne crée PAS de request ici!
      // L'entrée sera créée uniquement par le webhook Stripe après paiement confirmé.
      // Le code affilié et l'email sont passés à Stripe via metadata.

      // Créer la session Stripe avec toutes les infos nécessaires
      const { data, error } = await supabase.functions.invoke('create-stripe-session', {
        body: {
          productType,
          affiliateCode,
          selectedCountries,
          customerEmail: email.trim().toLowerCase(),
        }
      });

      if (error) {
        console.error("Erreur Supabase:", error);
        throw new Error(error.message);
      }

      if (!data?.success || !data?.url) {
        throw new Error(data?.error || "Erreur lors de la création de la session");
      }

      console.log("✅ Redirection vers Stripe:", data.url);
      
      // Fermer le dialogue et rediriger vers Stripe Checkout
      setEmailDialogOpen(false);
      window.location.href = data.url;
      
    } catch (error) {
      console.error("Erreur paiement:", error);
      toast.error("Erreur lors du paiement. Veuillez réessayer.");
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setEmailDialogOpen(false);
    setEmail("");
    setEmailError("");
    setSelectedCountries([]);
    setStep("countries");
    setIsLoading(false);
  };

  const handleBackToCountries = () => {
    setEmailDialogOpen(false);
    setStep("countries");
    setCountryModalOpen(true);
  };

  const getProductName = () => {
    switch (productType) {
      case "3months": return "Abonnement 3 mois";
      case "6months": return "Abonnement 6 mois";
      case "12months": return "Abonnement 12 mois VIP";
      case "test1euro": return "Test 1€";
      default: return "Abonnement";
    }
  };

  return (
    <>
      <Button
        onClick={handleButtonClick}
        className={className}
        size={size}
        variant={variant}
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Prise en charge de votre commande...
          </>
        ) : (
          children
        )}
      </Button>

      {/* Étape 1: Sélection des pays */}
      <CountrySelectionModal
        open={countryModalOpen && step === "countries"}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setCountryModalOpen(false);
          }
        }}
        onConfirm={handleCountriesConfirm}
        title="Choisissez vos pays"
      />

      {/* Étape 2: Collecte de l'email */}
      <Dialog open={emailDialogOpen && step === "email"} onOpenChange={(isOpen) => !isOpen && handleClose()}>
        <DialogContent className="bg-card border-border text-foreground max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl">{getProductName()}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleEmailSubmit} className="space-y-4">
            {/* Bouton retour + résumé pays */}
            <div className="flex items-start gap-3">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={handleBackToCountries}
                className="flex-shrink-0"
                disabled={isLoading}
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div className="flex-1">
                <p className="text-sm text-muted-foreground mb-1">Pays sélectionnés :</p>
                <p className="text-sm text-foreground font-medium">
                  {getCountryNames(selectedCountries)}
                </p>
              </div>
            </div>

            <p className="text-muted-foreground">
              Entrez votre email pour finaliser votre commande. Vous serez ensuite redirigé vers le paiement sécurisé.
            </p>
            
            <div className="space-y-2">
              <Label htmlFor="checkout-email">Votre email</Label>
              <Input
                id="checkout-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.com"
                className="bg-input border-border"
                required
                disabled={isLoading}
              />
              {emailError && <p className="text-destructive text-sm">{emailError}</p>}
            </div>
            
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Redirection vers le paiement...
                </>
              ) : (
                "Continuer vers le paiement sécurisé"
              )}
            </Button>
            
            <p className="text-xs text-muted-foreground text-center">
              🔒 Paiement 100% sécurisé par Stripe
            </p>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default StripeButtonWithCountries;
